import { CanvasJSON } from '../../canvas/types';
import { TRANSPARENT_PIXEL } from '../../shared/constants/transparent-pixel';

// Forces crossOrigin: 'anonymous' and upgrades http: to https: on every image.
// Both prevent canvas tainting, which would block reading pixels back out.
export function withCrossOrigin(json: CanvasJSON): CanvasJSON {
  if (!json?.objects) return json;
  return { ...json, objects: mapObjectsRecursive(json.objects) };
}

// Images nested in Groups taint the canvas too; walk the whole tree.
function mapObjectsRecursive<T extends Record<string, unknown>>(objects: T[]): T[] {
  return objects.map(obj => {
    const patched = withCrossOriginIfImage(obj);
    if (Array.isArray(patched.objects)) {
      return { ...patched, objects: mapObjectsRecursive(patched.objects as T[]) };
    }
    return patched;
  });
}

function withCrossOriginIfImage<T extends Record<string, unknown>>(obj: T): T {
  const type = (obj as { type?: string }).type;
  const isImage = type === 'image' || type === 'Image' || type === 'FabricImage';
  if (!isImage) return obj;

  const src = (obj as { src?: string }).src;
  const videoSrc = (obj as { videoSrc?: string }).videoSrc;

  // Video-backed image: Chrome no puede cargar un .mp4 como <img>, lo que hace
  // que fabric no cree el objeto y el canvas quede en blanco. Usamos un placeholder
  // transparente; hydrateCanvasVideos reemplaza el elemento con un <video> real
  // usando videoSrc después de loadFromJSON.
  if (videoSrc) {
    return { ...obj, crossOrigin: 'anonymous', src: TRANSPARENT_PIXEL };
  }

  // An empty src resolves to the current page URL and fails to load (and, once upgraded to
  // https, throws an uncaught enliven error). Use a transparent placeholder — same as videos.
  if (!src) {
    return { ...obj, crossOrigin: 'anonymous', src: TRANSPARENT_PIXEL };
  }

  const upgradedSrc =
    typeof src === 'string' && src.startsWith('http://') ? src.replace(/^http:\/\//, 'https://') : src;
  return { ...obj, crossOrigin: 'anonymous', src: upgradedSrc };
}
